// src/context/AuthContext.js

import React, { useContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";

const AuthContext = React.createContext();

const JWT_KEY = "sso_jwt_token";

export function useAuth() {
  return useContext(AuthContext);
}

// دالة مساعدة للتحقق من انتهاء صلاحية JWT
function isJwtValid(token) {
  try {
    const decoded = jwtDecode(token);
    if (!decoded.exp) return false;
    const now = Date.now() / 1000;
    return decoded.exp > now;
  } catch (err) {
    return false;
  }
}

// دالة لاستخراج بيانات المستخدم من الـJWT
function getUserFromJwt(token) {
  try {
    const decoded = jwtDecode(token);
    return {
      uid: decoded.userId,
      email: decoded.email,
      role: decoded.role,
      isActive: decoded.isActive,
      projectId: decoded.projectId,
      name: decoded.name,
      referralCode: decoded.referralCode || "",
    };
  } catch (err) {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [jwtToken, setJwtToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // (اختياري) تخزين آخر رابط نسخة أصلية تم توليده
  const [lastRedirectUrl, setLastRedirectUrl] = useState(null);

  // دالة تسجيل الخروج - تمسح كل شيء
  const logout = () => {
    localStorage.removeItem(JWT_KEY);
    setJwtToken(null);
    setCurrentUser(null);
    setLastRedirectUrl(null);
    setLoading(false);
  };

  // دالة استقبال وتخزين JWT (من URL أو بعد login/register)
  const loginWithJwt = (token) => {
    if (token && isJwtValid(token)) {
      localStorage.setItem(JWT_KEY, token);
      setJwtToken(token);
      const user = getUserFromJwt(token);
      setCurrentUser(user);
      setLoading(false);
    } else {
      logout();
    }
  };

  // دالة مساعدة: جلب بيانات النسخة الأصلية/الرابط/المشروع من currentUser (لبناء redirect URL)
  // يمكنك استخدامها في أي مكان في السيستم
  const getLandingRedirectInfo = () => {
    if (!currentUser || !currentUser.projectId) return null;
    return {
      projectId: currentUser.projectId,
      referralCode: currentUser.referralCode || "",
      uid: currentUser.uid,
      name: currentUser.name || "",
      email: currentUser.email || "",
    };
  };

  // عند كل تحميل للصفحة: التحقق من وجود JWT في الـlocalStorage أو في الـURL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jwtFromUrl = params.get("jwt");

    if (jwtFromUrl && isJwtValid(jwtFromUrl)) {
      loginWithJwt(jwtFromUrl);
      params.delete("jwt");
      const cleanUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "");
      window.history.replaceState({}, document.title, cleanUrl);
      return;
    }

    const jwtStored = localStorage.getItem(JWT_KEY);
    if (jwtStored && isJwtValid(jwtStored)) {
      setJwtToken(jwtStored);
      setCurrentUser(getUserFromJwt(jwtStored));
      setLoading(false);
    } else {
      logout();
    }
  }, []);

  // التحقق تلقائيًا من صلاحية JWT عند أي تحديث للصفحة/التوكن (Auto-logout)
  useEffect(() => {
    if (jwtToken) {
      const interval = setInterval(() => {
        if (!isJwtValid(jwtToken)) {
          logout();
        }
      }, 30000); // تحقق كل 30 ثانية (يمكنك تعديلها)
      return () => clearInterval(interval);
    }
  }, [jwtToken]);

  // تحديث السياق عند أي تغير في المستخدم
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
    }
  }, [currentUser]);

  const value = {
    currentUser,
    setCurrentUser,
    jwtToken,
    loginWithJwt,
    logout,
    getLandingRedirectInfo, // الدالة الجديدة
    lastRedirectUrl, // متغير التخزين الاختياري (يمكنك تعديله/تحديثه في LoginPage أو الداشبورد)
    setLastRedirectUrl,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}